import React from 'react';

import { Button, Group, Modal, Stack, Text, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import useDependentToLinked from 'hooks/athlete/connected/useDependentToLinked';
import { ConnectedAthlete } from 'types';
import notificationHandler from 'utils/notifications';

interface IDependentToLinkedModalProps {
  athlete: ConnectedAthlete | null;
  opened: boolean;
  closeHandler: () => void;
}

interface FormValues {
  email: string;
}

const DependentToLinkedModal = ({
  athlete,
  opened,
  closeHandler,
}: IDependentToLinkedModalProps): JSX.Element => {
  const dependentToLinkedMutation = useDependentToLinked();

  const form = useForm<FormValues>({
    initialValues: {
      email: '',
    },

    validate: {
      email: value =>
        value.length < 3 || value.length > 120 ? 'Email must be between 3-120 characters' : null,
    },
  });

  const handleCloseAndReset = (): void => {
    closeHandler();
    form.reset();
  };

  const handleSubmit = (values: FormValues): void => {
    const { successNotification, errorNotification } = notificationHandler();
    dependentToLinkedMutation.mutate(
      { athleteId: athlete?.athleteId || '', email: values.email },
      {
        onSuccess: () => {
          successNotification('New login email set successfully');
          closeHandler();
        },
        onError: (e: any) => {
          errorNotification(`Something went wrong: ${e.response.data.message}`);
        },
      },
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={handleCloseAndReset}
      title={<Title order={4}>Promote to Linked?</Title>}
      size='lg'
      trapFocus
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <Stack spacing='xs'>
            <Text size='sm'>{`Performing this action will allow this user to log into and manage their account.`}</Text>
            <Text size='sm'>{`The primary profile will no longer be able to log in as this athlete.`}</Text>
            <Text size='sm'>{`This athlete will continue to use the payment methods of the primary profile.`}</Text>
          </Stack>
          <TextInput
            label='Email'
            type='email'
            required
            placeholder='Please enter the email address this athlete will use to login.'
            {...form.getInputProps('email')}
          />
          <Group position='right'>
            <Button
              variant='subtle'
              onClick={handleCloseAndReset}
              disabled={dependentToLinkedMutation.isLoading}
            >
              Cancel
            </Button>
            <Button type='submit' loading={dependentToLinkedMutation.isLoading} data-cy='unlinkContinue'>
              Continue
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default DependentToLinkedModal;
